import { Platform } from "react-native";
/* -----------------Globals--------------- */
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { SessionAnalysis } from "@memorang/features";
/* -----------------UI--------------- */
import ReportCardsContainer from "./ReportCardsContainer";
import ReviewAnswersDialog, {
	type CheckboxStateMap,
} from "./dialogs/ReviewAnswersDialog";

/* -----------------Helpers & Hooks--------------- */
import { getReportData } from "../helpers/report-data";

/* -----------------Types--------------- */
import type { ReportDetails } from "@memorang/types";

/* -----------------Child components--------------- */

import { useExamContext } from "@features/exam";
import { currentAppVersion } from "@helpers/expo-extras";
import { useAppStore } from "@hooks/useAppStore";
import { ScrollContainer, StudySessionSummaryCard } from "@memorang/ui";

import { isMockedData, mockedReport } from "@constants/demo";
import { events } from "@constants/tracking";
import { formatDate } from "@helpers/date";
import { useAppTheme } from "@hooks/useAppTheme";
import useClerkUser from "@hooks/useClerkUser";
import { useDemoExamStore } from "@hooks/useDemoExamStore";
import { trackCustomEvent } from "analytics";
import { router } from "expo-router";
import type { ComponentProps, ReactElement } from "react";
import { useTranslation } from "react-i18next";
import EMRCard from "../../session/components/emr-card/EMRCard";
import { useEMRStore } from "../../session/components/emr-card/useEMRStore";
import ProethicaReportView from "./ProethicaReportView";

type Props = {
	reportDetails: ReportDetails;
	showReviewDialog: boolean;
	handleContinue: () => void;
	handleClickActionChip: (action: string) => void;
	checkboxStateMap: CheckboxStateMap;
	handleChangeCheckboxStateMap: (key: keyof CheckboxStateMap) => void;
	handleReviewButtonClick: () => void;
	handleCloseReviewDialog: () => void;
	selectedItemTypeForReview?: "QUESTION";
	handleReview: (checkboxStateMap: CheckboxStateMap) => void;
	fetchingReviewItems: boolean;
	isSummativeTest?: boolean;
	handleUpdateIncorrectCheckboxStateMap: () => void;
	handleTracking: (
		eventName: string,
		params?: Record<string, string | number>,
	) => void;
};
export const ReportView = ({
	reportDetails,
	showReviewDialog,
	handleContinue,
	checkboxStateMap,
	isSummativeTest,
	handleChangeCheckboxStateMap,
	handleCloseReviewDialog,
	selectedItemTypeForReview,
	handleReview,
	fetchingReviewItems,
	handleTracking,
	handleUpdateIncorrectCheckboxStateMap,
}: Props) => {
	const { t, i18n } = useTranslation();
	const theme = useAppTheme();

	const { summaryCardData, reviewListItemsData, sessionId } = getReportData(
		reportDetails,
		t,
		theme,
		isSummativeTest,
	);
	const currentDemoExam = useDemoExamStore((state) => state.currentExam);

	const userData = useEMRStore((state) => state.userData);
	const goldStandardData = useEMRStore((state) => state.goldStandardData);

	const { username, email } = useClerkUser();

	const {
		currentExamContext: { examName },
	} = useExamContext();

	// const { hasPracticeTestAccess, hasStudyPackAccess, showStudyPackViews } =
	// 	useProductAccess();

	const companyName = useAppStore((store) => store.companyName);
	const appName = useAppStore((store) => store.app.name);
	const scoreRanges = useAppStore((store) => store.app.scoreRanges);
	const tenantName = useAppStore((store) => store.tenant.name);
	const appFont = useAppStore((app) => app.app.font);
	const fontSize = useAppStore((store) => store.app.fontSizes?.stem) || 16;

	const productName = t("report.productName", {
		examName,
		type: isSummativeTest ? t("report.practiceTests") : t("report.studyPack"),
	});

	const insets = useSafeAreaInsets();

	const selectedReviewListItemsData =
		reviewListItemsData?.[selectedItemTypeForReview!];

	const _handleClickCard = (title: string) => {
		trackCustomEvent({
			eventName: events.sessionReportCardClicked,
			sessionId: sessionId,
			type: title,
		});
		router.push({
			pathname: "/(protected)/session/[id]/report/detailed-list/mastery",
			params: {
				type: title,
				id: sessionId,
				examName: reportDetails?.examName,
			},
		});
	};
	const sessionAnalysisProps: ComponentProps<typeof SessionAnalysis> = {
		fontSize,
		appFont,
		sessionId,
		demoExamId: currentDemoExam?.id,
		context: currentDemoExam?.name,
		handleTracking,
		userData,
		goldStandardData,
		sessionMetadata: {
			sessionTitle: reportDetails.examName,
			sessionDate: isMockedData
				? mockedReport.sessionDate
				: formatDate(reportDetails.sessionDate),
			numItems: reportDetails.numQuestions,
		},
		outputLanguage: i18n.language,
		feedbackDialogParams: {
			currentAppVersion,
			userName: username!,
			email,
			appLanguage: i18n.language,
			product: productName,
			appName,
			platform: Platform.OS.toUpperCase(),
			view: "Session Report",
			tenantId: tenantName,
			context: examName,
			feature: "AI Session Analysis",
			sessionId: `${sessionId}`,
			companyName,
		},
	};

	const isProethica = currentDemoExam?.id === "proethica";

	if (isProethica) {
		return (
			<>
				<ProethicaReportView
					sessionTitle={reportDetails.examName}
					sessionAnalysisProps={sessionAnalysisProps}
				/>
				{showReviewDialog && (
					<ReviewAnswersDialog
						open={showReviewDialog}
						handleChangeCheckboxStateMap={handleChangeCheckboxStateMap}
						checkboxStateMap={checkboxStateMap}
						sections={selectedReviewListItemsData?.items?.sections!}
						handleClose={handleCloseReviewDialog}
						handleReview={handleReview}
						loading={fetchingReviewItems}
						numIncorrect={selectedReviewListItemsData?.numIncorrect}
						handleUpdateIncorrectCheckboxStateMap={
							handleUpdateIncorrectCheckboxStateMap
						}
					/>
				)}
			</>
		);
	}

	const cards = [
		<StudySessionSummaryCard
			{...summaryCardData}
			stats={[]}
			handleContinue={handleContinue}
			scoreRanges={scoreRanges!}
			key={`${sessionId}-summary-card`}
		/>,

		<SessionAnalysis
			{...sessionAnalysisProps}
			key={`${sessionId}-session-analysis`}
		/>,
		currentDemoExam.showEMR
			? [<EMRCard key={`${sessionId}-emr-card`} showTooltip />]
			: [],
	];

	return (
		<ScrollContainer
			contentContainerStyle={{
				paddingBottom: 88 + insets.bottom,
				paddingHorizontal: 8,
				gap: 16,
			}}
		>
			<ReportCardsContainer cards={cards as ReactElement[]} />
			{showReviewDialog && (
				<ReviewAnswersDialog
					open={showReviewDialog}
					handleChangeCheckboxStateMap={handleChangeCheckboxStateMap}
					checkboxStateMap={checkboxStateMap}
					sections={selectedReviewListItemsData?.items?.sections!}
					handleClose={handleCloseReviewDialog}
					handleReview={handleReview}
					loading={fetchingReviewItems}
					numIncorrect={selectedReviewListItemsData?.numIncorrect}
					handleUpdateIncorrectCheckboxStateMap={
						handleUpdateIncorrectCheckboxStateMap
					}
				/>
			)}
		</ScrollContainer>
	);
};
